import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import padelService, { type PadelGameDetail } from '@/services/padelService';
import {
  PADEL_SET_PICKER_MAX,
  applyScoreValue,
  applySideDelta,
  buildSetsFromGame,
  canFillSet,
  countSetWins,
  resolveAmericanoScores,
  type SetScoreInput,
} from '@/components/community/padel/padelScoringUtils';
import {
  INITIAL_RALLY_STATE,
  MAX_DEUCE_GAMES,
  canBumpTennisScore,
  isTennisSetComplete,
  isValidTennisGameScore,
  nextTennisRallyPoint,
  recordGameWinToSet,
  type TennisRallyState,
} from '@/components/community/padel/padelTennisScoring';
import { hostPaths } from '@/utils/hostPaths';
import { clientPaths, isCustomerAppPath } from '@/utils/clientPaths';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { impactLight, notifyError } from '@/utils/nativeHaptics';
import { historyCanGoBack } from '@/utils/nativeNavigation';
import showToast from '@/components/ui/Toast';

export function usePadelEditSkor() {
  const { slug, gameSlug } = useParams<{ slug: string; gameSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isClient = isCustomerAppPath(location.pathname);
  const gameNumber = (location.state as { gameNumber?: number } | null)?.gameNumber ?? 1;
  const [game, setGame] = useState<PadelGameDetail | null>(null);
  const [sets, setSets] = useState<SetScoreInput[]>([]);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [activeSet, setActiveSet] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [receivedAt, setReceivedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const backPath =
    isClient && slug
      ? clientPaths.event.padel(slug)
      : slug
        ? hostPaths.mabar.padel(slug)
        : hostPaths.mabar.list;
  const goBack = () => {
    if (historyCanGoBack()) {
      navigate(-1);
      return;
    }
    navigate(backPath);
  };

  useEffect(() => {
    if (!gameSlug) return;
    padelService
      .getGame(gameSlug)
      .then((detail) => {
        setGame(detail);
        setScoreA(detail.scoreSideA ?? 0);
        setScoreB(detail.scoreSideB ?? 0);
        setSets(buildSetsFromGame(detail.setScores ?? [], detail.maxSets ?? 3));
        setActiveSet(detail.timerSetNumber || 1);
        setReceivedAt(Date.now());
      })
      .catch((err: unknown) => setErrorMessage(getApiErrorMessage(err, 'Gagal memuat game')))
      .finally(() => setLoading(false));
  }, [gameSlug]);

  useEffect(() => {
    if (game?.timerStatus !== 'running') return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [game?.timerStatus]);

  const [rallyState, setRallyState] = useState<TennisRallyState>(INITIAL_RALLY_STATE);
  const [manualEntry, setManualEntry] = useState(false);
  const [deuceEnabled, setDeuceEnabled] = useState(false);
  const [rallyHistory, setRallyHistory] = useState<
    Array<{
      rally: TennisRallyState;
      sets: SetScoreInput[];
      activeSet: number;
      deuceEnabled: boolean;
    }>
  >([]);

  const isPoints = game?.scoringType !== 'sets';
  const target = game?.targetScore ?? 21;
  const maxSets = game?.maxSets ?? 3;
  const current = sets.find((s) => s.setNumber === activeSet) ?? {
    setNumber: activeSet,
    scoreSideA: 0,
    scoreSideB: 0,
  };
  const { winsA, winsB } = countSetWins(sets);
  const running = game?.timerStatus === 'running' && (isPoints || game.timerSetNumber === activeSet);

  const applyAmericano = (side: 'a' | 'b', value: number) => {
    const next = resolveAmericanoScores(side, value, scoreA, scoreB, target);
    setScoreA(next.scoreA);
    setScoreB(next.scoreB);
  };

  const bump = (side: 'a' | 'b', delta: number) => {
    impactLight();
    if (!game) return;
    if (isPoints) {
      applyAmericano(side, Math.max(0, (side === 'a' ? scoreA : scoreB) + delta));
      return;
    }
    if (delta > 0 && !canFillSet(activeSet, sets, maxSets)) return;
    if (!canBumpTennisScore(current.scoreSideA, current.scoreSideB, side, delta, deuceEnabled)) {
      if (delta > 0 && isTennisSetComplete(current.scoreSideA, current.scoreSideB, deuceEnabled)) {
        showToast.info(`Set ${activeSet} sudah selesai. Tidak dapat menambah game.`);
      }
      return;
    }
    const cap = deuceEnabled ? MAX_DEUCE_GAMES : PADEL_SET_PICKER_MAX;
    const next = applySideDelta(
      current.scoreSideA,
      current.scoreSideB,
      side,
      delta,
      cap
    );
    setSets((prev) => prev.map((s) => (s.setNumber === activeSet ? { ...s, ...next } : s)));
  };

  const setAbsoluteScore = (side: 'a' | 'b', value: number) => {
    if (!game) return;
    if (isPoints) {
      applyAmericano(side, value);
      return;
    }
    if (!canFillSet(activeSet, sets, maxSets)) return;
    const cap = deuceEnabled ? MAX_DEUCE_GAMES : PADEL_SET_PICKER_MAX;
    const clampedValue = Math.min(cap, Math.max(0, value));
    const opp = side === 'a' ? current.scoreSideB : current.scoreSideA;
    if (clampedValue > 0 || opp > 0) {
      const candA = side === 'a' ? clampedValue : opp;
      const candB = side === 'b' ? clampedValue : opp;
      if (!isValidTennisGameScore(candA, candB, deuceEnabled)) {
        showToast.error('Kombinasi skor tidak sesuai aturan tenis');
        return;
      }
    }
    const next = applyScoreValue(
      current.scoreSideA,
      current.scoreSideB,
      side,
      clampedValue,
      cap
    );
    setSets((prev) => prev.map((s) => (s.setNumber === activeSet ? { ...s, ...next } : s)));
  };

  const toggleDeuce = (enabled: boolean) => {
    impactLight();
    setDeuceEnabled(enabled);
    if (!enabled) {
      if (rallyState.pointA === 'AD' || rallyState.pointB === 'AD') {
        setRallyState((prev) => ({
          ...prev,
          pointA: '40',
          pointB: '40',
        }));
      }
    }
  };

  const addRallyPoint = (side: 'a' | 'b') => {
    impactLight();
    if (!game || isPoints) return;
    if (!canFillSet(activeSet, sets, maxSets)) return;
    if (isTennisSetComplete(current.scoreSideA, current.scoreSideB, deuceEnabled)) {
      showToast.info(`Set ${activeSet} sudah selesai. Pindah ke set berikutnya atau simpan skor.`);
      return;
    }

    setRallyHistory((prev) => [...prev, { rally: rallyState, sets, activeSet, deuceEnabled }]);

    const step = nextTennisRallyPoint(rallyState, side, deuceEnabled);
    if (step.gameWon) {
      const winnerSide = step.winnerSide || side;
      const gameRecord = recordGameWinToSet(current.scoreSideA, current.scoreSideB, winnerSide, deuceEnabled);
      const updatedSets = sets.map((s) =>
        s.setNumber === activeSet
          ? { ...s, scoreSideA: gameRecord.nextGamesA, scoreSideB: gameRecord.nextGamesB }
          : s
      );
      setSets(updatedSets);

      if (gameRecord.isSetComplete) {
        showToast.success(
          `Set ${activeSet} selesai (${gameRecord.nextGamesA}-${gameRecord.nextGamesB})!`
        );
        setRallyState(INITIAL_RALLY_STATE);
        if (maxSets === 3 && activeSet < 3 && canFillSet(activeSet + 1, updatedSets, maxSets)) {
          setActiveSet((prev) => prev + 1);
        }
      } else {
        setRallyState({
          ...INITIAL_RALLY_STATE,
          isTiebreak: gameRecord.isTiebreakNext,
        });
      }
    } else {
      setRallyState(step.nextRally);
    }
  };

  const undoRallyPoint = () => {
    impactLight();
    setRallyHistory((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRallyState(last.rally);
      setSets(last.sets);
      setActiveSet(last.activeSet);
      setDeuceEnabled(last.deuceEnabled);
      return prev.slice(0, -1);
    });
  };

  const patchTimer = async (action: 'start' | 'pause' | 'reset') => {
    if (!gameSlug) return;
    impactLight();
    try {
      const detail = await padelService.updateTimer(gameSlug, {
        action,
        setNumber: isPoints ? 1 : activeSet,
      });
      setGame(detail);
      setReceivedAt(Date.now());
    } catch (err: unknown) {
      notifyError();
      setErrorMessage(getApiErrorMessage(err, 'Gagal memperbarui timer'));
    }
  };

  const handleSave = async () => {
    if (!gameSlug || !slug) return;
    if (!isPoints) {
      for (const s of sets) {
        if (s.scoreSideA > 0 || s.scoreSideB > 0) {
          if (!isValidTennisGameScore(s.scoreSideA, s.scoreSideB, deuceEnabled)) {
            setErrorMessage(
              `Set ${s.setNumber}: Skor ${s.scoreSideA}-${s.scoreSideB} tidak valid menurut regulasi tenis.`
            );
            return;
          }
        }
      }
    }
    setSaving(true);
    try {
      await padelService.saveScores(
        gameSlug,
        isPoints
          ? { matchScore: { scoreSideA: scoreA, scoreSideB: scoreB } }
          : { sets: sets.map((s) => ({ setNumber: s.setNumber, scoreSideA: s.scoreSideA, scoreSideB: s.scoreSideB })) }
      );
      const summary = isPoints
        ? `${scoreA}-${scoreB}`
        : sets
            .filter((s) => s.scoreSideA > 0 || s.scoreSideB > 0)
            .map((s) => `${s.scoreSideA}-${s.scoreSideB}`)
            .join(', ');
      showToast.success(`Game ${gameNumber} · ${summary}. Klasemen ikut diperbarui.`);
      navigate(backPath);
    } catch (err: unknown) {
      notifyError();
      setErrorMessage(getApiErrorMessage(err, 'Gagal menyimpan skor'));
    } finally {
      setSaving(false);
    }
  };

  return {
    game,
    gameNumber,
    sets,
    scoreA,
    scoreB,
    activeSet,
    setActiveSet,
    loading,
    saving,
    errorMessage,
    receivedAt,
    now,
    backPath,
    goBack,
    isPoints,
    target,
    maxSets,
    current,
    winsA,
    winsB,
    running,
    bump,
    setAbsoluteScore,
    patchTimer,
    handleSave,
    navigate,
    rallyState,
    manualEntry,
    setManualEntry,
    deuceEnabled,
    toggleDeuce,
    addRallyPoint,
    undoRallyPoint,
    canUndo: rallyHistory.length > 0,
  };
}
